import { useEffect, useMemo, useState } from "react";
import { MessageCircle, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { buildTemplates, fillTemplate, waLink } from "@/lib/constants";
import { useSettings } from "@/lib/settings";

export const WhatsAppDialog = ({ open, onOpenChange, name, phone, template = "consulta", vars = {} }) => {
  const settings = useSettings();
  const templates = useMemo(() => buildTemplates(settings), [settings]);
  const [tpl, setTpl] = useState(template);
  const [text, setText] = useState("");
  const varsKey = JSON.stringify(vars);

  useEffect(() => {
    if (open) setTpl(template);
  }, [open, template]);

  useEffect(() => {
    const t = templates.find((x) => x.id === tpl) || templates[0];
    setText(fillTemplate(t.text, { nome: (name || "").split(" ")[0], ...JSON.parse(varsKey) }));
  }, [tpl, templates, name, varsKey]);

  const hasPhone = (phone || "").replace(/\D/g, "").length >= 10;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-xl">
            <MessageCircle className="h-5 w-5 text-emerald-600" /> Mensagem no WhatsApp
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <p className="text-sm text-slate-600">Para <b>{name}</b> · {phone || "sem telefone cadastrado"}</p>
          <div className="grid gap-1.5">
            <Label>Modelo</Label>
            <Select value={tpl} onValueChange={setTpl}>
              <SelectTrigger data-testid="whatsapp-template-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Mensagem</Label>
            <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="whatsapp-message-input" />
          </div>
          {!hasPhone && <p className="text-xs text-rose-600" data-testid="whatsapp-no-phone">Cadastre um telefone válido com DDD para enviar.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Fechar</Button>
          <Button asChild={hasPhone} disabled={!hasPhone} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-send-btn">
            {hasPhone ? (
              <a href={waLink(phone, text)} target="_blank" rel="noopener noreferrer">
                Abrir WhatsApp <ExternalLink className="ml-1.5 h-4 w-4" />
              </a>
            ) : <span>Abrir WhatsApp</span>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
